import React, { createContext, useContext, useState, useEffect } from 'react';
import en from '../i18n/en.json';
import zh from '../i18n/zh.json';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState('zh'); // Default to zh for current demo
  const [translations, setTranslations] = useState(zh);

  // Function to change language
  const changeLanguage = async (newLang) => {
    try {
      // Update local state first for immediate feedback
      setLang(newLang);
      setTranslations(newLang === 'en' ? en : zh);
      
      // Update HTML lang attribute for accessibility
      document.documentElement.setAttribute('lang', newLang);
      // Update document title
      document.title = newLang === 'en'
        ? 'EOATO AI Retail Business Management System'
        : 'EOATO人工智能零售业务管理系统';
      
      // Save to localStorage as fallback
      localStorage.setItem('user_language', newLang);
    } catch (error) {
      console.error('Error changing language:', error);
      // Revert on error
      const currentLang = localStorage.getItem('user_language') || 'zh';
      setLang(currentLang);
      setTranslations(currentLang === 'en' ? en : zh);
    }
  };

  useEffect(() => {
    // Try to get language from multiple sources in order of priority
    const loadLanguage = async () => {
      let finalLang = 'zh'; // Default fallback
      
      // 1. Check localStorage first (fastest)
      const storedLang = localStorage.getItem('user_language');
      if (storedLang && (storedLang === 'zh' || storedLang === 'en')) {
        finalLang = storedLang;
      } else {
        // 2. Try to fetch from API
        try {
          const response = await fetch('/api/settings/params');
          if (response.ok) {
            try {
              const data = await response.json();
              if (data.status === 'success' && data.data.language) {
                // Only accept 'en' or 'zh', default to 'zh' for any other value
                finalLang = data.data.language === 'en' ? 'en' : 'zh';
                // Store in localStorage for future
                localStorage.setItem('user_language', finalLang);
              }
            } catch (jsonError) {
              console.warn('Failed to parse language API response, using default');
            }
          } else if (response.status === 401) {
            // 401 is expected when not logged in, use default
            console.warn('Language API returned 401 (not logged in), using default');
          } else {
            console.warn('Language API returned non-OK status:', response.status);
          }
        } catch (error) {
          console.warn('Failed to fetch language from API, using default');
        }
      }
      
      // Apply the language
      setLang(finalLang);
      setTranslations(finalLang === 'en' ? en : zh);
      
      // Update HTML lang attribute for accessibility
      document.documentElement.setAttribute('lang', finalLang);
      // Update document title
      document.title = finalLang === 'en'
        ? 'EOATO AI Retail Business Management System'
        : 'EOATO人工智能零售业务管理系统';
    };
    
    loadLanguage();
  }, []);

  const t = (path, variables = {}) => {
    const keys = path.split('.');
    let result = translations;
    for (const key of keys) {
      if (result[key]) {
        result = result[key];
      } else {
        return path; // Fallback to key if not found
      }
    }
    
    if (typeof result === 'string') {
      Object.keys(variables).forEach(v => {
        result = result
          .replace(`{{${v}}}`, variables[v])
          .replace(`{${v}}`, variables[v]);
      });
    }
    return result;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => useContext(LanguageContext);
